<script setup lang="ts">
import {
  UserButton,
  SignInButton,
  SignUpButton,
  useAuth,
  useUser,
} from "@clerk/vue";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import LucideIcon from "@/components/LucideIcon.vue";
import ThemeToggle from "@/components/ThemeToggle.vue";
import { PandoTooltip } from "@/components/ui/tooltip";
import { useNavigation } from "@/composables/useNavigation";

defineProps<{
  expanded: boolean;
}>();

const emit = defineEmits<{
  "update:expanded": [value: boolean];
}>();

const route = useRoute();
const router = useRouter();
const auth = useAuth();
const { user } = useUser();
const { navigationItems } = useNavigation();

// Track expanded sections (auto-expand Activity by default)
const expandedSections = ref<Set<string>>(new Set(["/activity"]));

const isActive = (path: string) => {
  return route.path === path || route.path.startsWith(path + "/");
};

const isChildActive = (path: string) => {
  return route.path === path;
};

const isExpanded = (path: string): boolean => {
  return expandedSections.value.has(path);
};

const toggleExpand = (path: string) => {
  if (expandedSections.value.has(path)) {
    expandedSections.value.delete(path);
  } else {
    expandedSections.value.add(path);
  }
};

const navigate = (path: string) => {
  router.push(path);
};

const handleItemClick = (item: { path: string; children?: unknown[] }) => {
  if (item.children?.length) {
    toggleExpand(item.path);
  } else {
    navigate(item.path);
  }
};

const handleSignOut = () => {
  auth?.signOut?.value?.();
};
</script>

<template>
  <aside
    :class="[
      'fixed left-0 top-0 z-40 h-screen bg-card border-r border-border transition-all duration-300 flex flex-col',
      expanded ? 'w-52' : 'w-16',
    ]"
  >
    <!-- Header -->
    <div
      :class="[
        'flex items-center border-b border-border h-16',
        expanded ? 'px-4 justify-between' : 'justify-center',
      ]"
    >
      <h2 v-if="expanded" class="text-lg font-semibold">Aspen</h2>
      <button
        class="p-2 rounded-md hover:bg-accent"
        :aria-label="expanded ? 'Collapse sidebar' : 'Expand sidebar'"
        @click="emit('update:expanded', !expanded)"
      >
        <LucideIcon :name="expanded ? 'chevrons-left' : 'chevrons-right'" />
      </button>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 py-4 overflow-y-auto">
      <template v-if="expanded">
        <template v-for="item in navigationItems" :key="item.path">
          <!-- Parent Item -->
          <button
            :class="[
              'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors',
              isActive(item.path) && !item.children?.length
                ? 'bg-accent text-accent-foreground border-l-2 border-primary'
                : 'hover:bg-accent hover:text-accent-foreground',
            ]"
            @click="handleItemClick(item)"
          >
            <LucideIcon :name="item.icon" :size="20" />
            <span class="truncate flex-1 text-left">{{ item.label }}</span>
            <LucideIcon
              v-if="item.children?.length"
              :name="isExpanded(item.path) ? 'ChevronDown' : 'ChevronRight'"
              :size="16"
              class="text-muted-foreground"
            />
          </button>

          <!-- Child Items -->
          <div
            v-if="item.children?.length && isExpanded(item.path)"
            class="ml-4 space-y-1 border-l border-border pl-3"
          >
            <button
              v-for="child in item.children.filter(
                (c: { show?: boolean }) => c.show !== false,
              )"
              :key="child.path"
              :class="[
                'w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors rounded-md',
                isChildActive(child.path)
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              ]"
              @click="navigate(child.path)"
            >
              <LucideIcon :name="child.icon" :size="16" />
              <span class="truncate">{{ child.label }}</span>
            </button>
          </div>
        </template>
      </template>
      <template v-else>
        <template v-for="item in navigationItems" :key="item.path">
          <PandoTooltip :content="item.label" side="right">
            <button
              :class="[
                'w-full flex items-center justify-center py-3 transition-colors',
                isActive(item.path)
                  ? 'bg-accent text-accent-foreground border-l-2 border-primary'
                  : 'hover:bg-accent hover:text-accent-foreground',
              ]"
              @click="
                item.children?.length
                  ? emit('update:expanded', true)
                  : navigate(item.path)
              "
            >
              <LucideIcon :name="item.icon" :size="20" />
            </button>
          </PandoTooltip>
        </template>
      </template>
    </nav>

    <!-- Bottom actions -->
    <div class="border-t border-border space-y-2">
      <!-- Theme Toggle -->
      <div class="p-2">
        <div v-if="expanded" class="px-2 flex items-center justify-between">
          <span class="text-sm text-muted-foreground">Theme</span>
          <ThemeToggle />
        </div>
        <div v-else class="flex justify-center">
          <ThemeToggle />
        </div>
      </div>

      <!-- User section (authenticated) -->
      <div
        v-if="auth?.userId?.value"
        :class="[
          'border-t border-border flex items-center',
          expanded ? 'px-4 py-3 gap-3 justify-between' : 'p-2 justify-center',
        ]"
      >
        <template v-if="expanded">
          <div class="flex items-center gap-3 min-w-0">
            <UserButton />
            <span class="text-sm text-muted-foreground truncate">
              {{
                user?.firstName ||
                user?.primaryEmailAddress?.emailAddress?.split("@")[0] ||
                "User"
              }}
            </span>
          </div>
          <PandoTooltip content="Sign Out">
            <button
              class="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
              @click="handleSignOut"
            >
              <LucideIcon name="log-out" :size="16" />
            </button>
          </PandoTooltip>
        </template>
        <template v-else>
          <PandoTooltip
            :content="
              user?.fullName ||
              user?.primaryEmailAddress?.emailAddress ||
              'User'
            "
            side="right"
          >
            <div>
              <UserButton />
            </div>
          </PandoTooltip>
        </template>
      </div>

      <!-- Auth buttons (unauthenticated) -->
      <div
        v-else
        :class="[
          'border-t border-border',
          expanded ? 'px-4 py-3 space-y-2' : 'p-2 space-y-2',
        ]"
      >
        <template v-if="expanded">
          <SignUpButton mode="modal">
            <button
              class="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              <LucideIcon name="user-plus" :size="16" />
              <span>Sign Up</span>
            </button>
          </SignUpButton>
          <SignInButton mode="modal">
            <button
              class="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent transition-colors"
            >
              <LucideIcon name="log-in" :size="16" />
              <span>Sign In</span>
            </button>
          </SignInButton>
        </template>
        <template v-else>
          <PandoTooltip content="Sign Up" side="right">
            <SignUpButton mode="modal">
              <button
                class="w-full flex items-center justify-center p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <LucideIcon name="user-plus" :size="20" />
              </button>
            </SignUpButton>
          </PandoTooltip>
          <PandoTooltip content="Sign In" side="right">
            <SignInButton mode="modal">
              <button
                class="w-full flex items-center justify-center p-2 rounded-md border border-border hover:bg-accent transition-colors"
              >
                <LucideIcon name="log-in" :size="20" />
              </button>
            </SignInButton>
          </PandoTooltip>
        </template>
      </div>
    </div>
  </aside>
</template>
